import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import toast from 'react-hot-toast';
import SignalKMonitor from './SignalKMonitor';
import {
  ServerIcon,
  KeyIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  SignalIcon,
  PauseIcon,
  PlayIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Description = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

const DataControlPanel = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${props => props.paused ? theme.colors.warning.light : theme.colors.success.light};
  border-radius: ${theme.borderRadius.md};
  margin-top: 1rem;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.variant === 'pause' ? theme.colors.warning.main : theme.colors.success.main};
  color: white;

  &:hover {
    background: ${props => props.variant === 'pause' ? theme.colors.warning.dark : theme.colors.success.dark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ControlInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ControlText = styled.div`
  color: ${props => props.paused ? theme.colors.warning.dark : theme.colors.success.dark};
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'active': return theme.colors.success.light;
      case 'inactive': return theme.colors.text.tertiary;
      case 'error': return theme.colors.error.light;
      default: return theme.colors.text.tertiary;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return theme.colors.success.dark;
      case 'inactive': return theme.colors.text.secondary;
      case 'error': return theme.colors.error.dark;
      default: return theme.colors.text.secondary;
    }
  }};
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
`;

const InfoLabel = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
`;

const InfoValue = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: 500;
  font-family: ${props => props.mono ? 'monospace' : 'inherit'};
`;

const TokenSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
`;

const TokenLabel = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const TokenDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.sm};
  font-family: monospace;
  font-size: 0.875rem;
  color: ${theme.colors.text.primary};
  word-break: break-all;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    if (props.variant === 'primary') {
      return `
        background: ${theme.colors.primary.main};
        color: white;
        &:hover {
          background: ${theme.colors.primary.dark};
          transform: translateY(-1px);
        }
      `;
    } else if (props.variant === 'danger') {
      return `
        background: ${theme.colors.error.main};
        color: white;
        &:hover {
          background: ${theme.colors.error.dark};
          transform: translateY(-1px);
        }
      `;
    } else {
      return `
        background: transparent;
        color: ${theme.colors.text.primary};
        border: 1px solid ${theme.colors.border.primary};
        &:hover {
          background: ${theme.colors.background.secondary};
          border-color: ${theme.colors.primary.main};
        }
      `;
    }
  }}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  padding: 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.primary.main};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
`;

const InstallGuide = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
`;

const CodeBlock = styled.pre`
  background: ${theme.colors.background.primary};
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  overflow-x: auto;
  font-family: monospace;
  font-size: 0.875rem;
  color: ${theme.colors.text.primary};
  margin: 1rem 0;
`;

const SignalKPluginManager = () => {
  const [vessels, setVessels] = useState([]);
  const [tokens, setTokens] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState(null);
  const [dataIngestionStatus, setDataIngestionStatus] = useState({
    active: true,
    loading: false
  });

  useEffect(() => {
    loadData();
    loadDataIngestionStatus();
    
    // Refresh stats and vessel tokens every 30 seconds
    const interval = setInterval(() => {
      loadStats();
      loadDataIngestionStatus();
      loadVessels(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/vessels');
      const list = response.data?.vessels ?? (Array.isArray(response.data) ? response.data : []);
      setVessels(list);
      setLoading(false);
      await Promise.allSettled([
        loadStats(),
        loadDataIngestionStatus(),
        ...list.map((v) => loadVesselToken(v.id)),
      ]);
    } catch (error) {
      console.error('Error loading plugin data:', error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        (error.response?.status === 401 ? 'Session expired — please log in again' : 'Failed to load vessels');
      toast.error(msg);
      setLoading(false);
    }
  };

  const loadVessels = async (isPoll = false) => {
    try {
      const response = await api.get('/api/vessels');
      const list = response.data?.vessels ?? (Array.isArray(response.data) ? response.data : []);
      setVessels(list);
      await Promise.allSettled(list.map((vessel) => loadVesselToken(vessel.id, isPoll)));
    } catch (error) {
      console.error('Error loading vessels:', error);
      if (!isPoll) {
        const msg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to load vessels';
        toast.error(msg);
      }
    }
  };

  const loadVesselToken = async (vesselId, isPoll = false) => {
    try {
      const response = await api.get(`/api/vessels/${vesselId}/token`, {
        params: isPoll ? { poll: 'true' } : undefined
      });
      setTokens(prev => ({
        ...prev,
        [vesselId]: response.data
      }));
    } catch (error) {
      console.error(`Error loading token for vessel ${vesselId}:`, error);
    }
  };

  const loadStats = async () => {
    // TODO: Implement stats endpoint
    // For now, use mock data
    setStats({
      totalDataPoints: 45623,
      lastIngestion: new Date().toISOString(),
      avgIngestionRate: 42,
      pluginVersion: '2.0.0'
    });
  };

  const loadDataIngestionStatus = async () => {
    try {
      const response = await api.get('/api/data-control/status');
      setDataIngestionStatus({
        active: response.data.data_ingestion_active,
        paused: response.data.paused,
        pausedAt: response.data.paused_at,
        pausedBy: response.data.paused_by,
        loading: false
      });
    } catch (error) {
      console.error('Error loading data ingestion status:', error);
    }
  };

  const handlePauseIngestion = async () => {
    try {
      setDataIngestionStatus(prev => ({ ...prev, loading: true }));
      await api.post('/api/data-control/pause');
      toast.success('✅ Data ingestion paused - Plugin running, data not stored');
      await loadDataIngestionStatus();
    } catch (error) {
      console.error('Error pausing data ingestion:', error);
      toast.error('Failed to pause data ingestion');
      setDataIngestionStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleResumeIngestion = async () => {
    try {
      setDataIngestionStatus(prev => ({ ...prev, loading: true }));
      await api.post('/api/data-control/resume');
      toast.success('Data ingestion resumed');
      await loadDataIngestionStatus();
    } catch (error) {
      console.error('Error resuming data ingestion:', error);
      toast.error('Failed to resume data ingestion');
      setDataIngestionStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const handleRegenerateToken = async (vesselId) => {
    try {
      const response = await api.post(`/api/vessels/${vesselId}/token/regenerate`);
      setTokens(prev => ({
        ...prev,
        [vesselId]: response.data
      }));
      toast.success('🔑 Token regenerated - Update your SignalK plugin configuration!', {
        duration: 6000
      });
    } catch (error) {
      console.error('Error regenerating token:', error);
      toast.error('Failed to regenerate token');
    }
  };

  const handleCopyToken = async (token, vesselId) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(vesselId);
      toast.success('Token copied to clipboard');
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      toast.error('Failed to copy token');
    }
  };

  const getPluginStatus = (token, streamConnected = false) => {
    if (!token) return 'inactive';

    if (streamConnected) return 'active';

    if (!token.last_used_at) return 'inactive';

    const lastUsed = new Date(token.last_used_at);
    if (Number.isNaN(lastUsed.getTime())) return 'inactive';

    const diffMinutes = (Date.now() - lastUsed.getTime()) / (1000 * 60);

    if (diffMinutes < 5) return 'active';
    if (diffMinutes < 1440) return 'inactive';
    return 'error';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircleIcon;
      case 'inactive': return ClockIcon;
      case 'error': return XCircleIcon;
      default: return ExclamationTriangleIcon;
    }
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <ArrowPathIcon className="w-8 h-8 mx-auto mb-2 animate-spin text-gray-400" />
          <p>Loading plugin data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderTop>
          <HeaderLeft>
            <Title>
              <ServerIcon className="w-6 h-6" />
              SignalK Plugin Manager
            </Title>
            <Description>
              Monitor and manage SignalK plugin installations on your vessels. The plugin pushes data directly 
              from your SignalK server to BoatWise Cloud - no VPN or port forwarding required.
            </Description>
          </HeaderLeft>
        </HeaderTop>

        <DataControlPanel paused={dataIngestionStatus.paused}>
          <ControlInfo>
            <CircleStackIcon className="w-6 h-6" />
            <div>
              <ControlText paused={dataIngestionStatus.paused}>
                {dataIngestionStatus.paused ? '⏸️  Data Ingestion PAUSED' : '▶️  Data Ingestion ACTIVE'}
              </ControlText>
              {dataIngestionStatus.paused && dataIngestionStatus.pausedAt && (
                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.8 }}>
                  Paused {new Date(dataIngestionStatus.pausedAt).toLocaleString()} by {dataIngestionStatus.pausedBy}
                </div>
              )}
              {!dataIngestionStatus.paused && (
                <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.8 }}>
                  Database is receiving and storing all SignalK data
                </div>
              )}
            </div>
          </ControlInfo>
          
          {dataIngestionStatus.paused ? (
            <ControlButton 
              onClick={handleResumeIngestion}
              disabled={dataIngestionStatus.loading}
            >
              <PlayIcon className="w-5 h-5" />
              Resume Ingestion
            </ControlButton>
          ) : (
            <ControlButton 
              variant="pause"
              onClick={handlePauseIngestion}
              disabled={dataIngestionStatus.loading}
            >
              <PauseIcon className="w-5 h-5" />
              Pause Ingestion
            </ControlButton>
          )}
        </DataControlPanel>
      </Header>

      {/* Live feed monitor — show as soon as vessels list is known */}
      {vessels.length > 0 && <SignalKMonitor vesselId={vessels[0].id} />}

      <Grid>
        {vessels.map(vessel => {
          const token = tokens[vessel.id];
          const status = getPluginStatus(token);
          const StatusIcon = getStatusIcon(status);

          return (
            <Card key={vessel.id}>
              <CardHeader>
                <CardTitle>
                  <SignalIcon className="w-5 h-5" />
                  {vessel.vessel_name}
                </CardTitle>
                <StatusBadge status={status}>
                  <StatusIcon className="w-4 h-4" />
                  {status === 'active' ? 'Active' : status === 'inactive' ? 'Inactive' : 'Error'}
                </StatusBadge>
              </CardHeader>

              <InfoGrid>
                {token && (
                  <>
                    <InfoRow>
                      <InfoLabel>Last Data Push:</InfoLabel>
                      <InfoValue>
                        {token.last_used_at 
                          ? new Date(token.last_used_at).toLocaleString()
                          : 'Never'}
                      </InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Total Pushes:</InfoLabel>
                      <InfoValue>{token.push_count?.toLocaleString() || 0}</InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Stream Connections:</InfoLabel>
                      <InfoValue>{token.stream_count?.toLocaleString() || 0}</InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Token Created:</InfoLabel>
                      <InfoValue>
                        {new Date(token.created_at).toLocaleDateString()}
                      </InfoValue>
                    </InfoRow>
                  </>
                )}
              </InfoGrid>

              {token && (
                <TokenSection>
                  <TokenLabel>
                    <KeyIcon className="w-4 h-4 inline mr-1" />
                    Vessel Token
                  </TokenLabel>
                  <TokenDisplay>
                    {token.token}
                  </TokenDisplay>
                  <ButtonGroup>
                    <Button onClick={() => handleCopyToken(token.token, vessel.id)}>
                      {copiedToken === vessel.id ? (
                        <>
                          <ClipboardDocumentCheckIcon className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <DocumentDuplicateIcon className="w-4 h-4" />
                          Copy Token
                        </>
                      )}
                    </Button>
                    <Button variant="danger" onClick={() => handleRegenerateToken(vessel.id)}>
                      <ArrowPathIcon className="w-4 h-4" />
                      Regenerate
                    </Button>
                  </ButtonGroup>
                </TokenSection>
              )}

              {!token && (
                <TokenSection>
                  <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                    No token generated for this vessel yet.
                  </p>
                </TokenSection>
              )}
            </Card>
          );
        })}
      </Grid>

      <InstallGuide>
        <CardTitle style={{ marginBottom: '1rem' }}>
          <ShieldCheckIcon className="w-5 h-5" />
          Plugin Installation Guide
        </CardTitle>
        
        <Description style={{ marginBottom: '1.5rem' }}>
          The BoatWise plugin runs on your SignalK server (Cerbo) and pushes data to the cloud.
          App Store install is not available yet — use SSH deploy below.
        </Description>

        <h4 style={{ color: theme.colors.text.primary, marginBottom: '0.5rem' }}>
          Install on Cerbo (from your Mac on the boat network)
        </h4>
        <p style={{ color: theme.colors.text.secondary, marginBottom: '0.5rem', fontSize: '0.875rem' }}>
          In the boatwise-platform repo:
        </p>
        <code style={{ display: 'block', color: theme.colors.text.secondary, fontSize: '0.8rem', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
{`cd signalk-to-boatwise-plugin
./deploy-to-cerbo.sh root@192.168.5.210`}
        </code>

        <h4 style={{ color: theme.colors.text.primary, marginBottom: '0.5rem' }}>
          Configure in SignalK Admin
        </h4>
        <p style={{ color: theme.colors.text.secondary, marginBottom: '0.5rem' }}>
          Open <strong>http://192.168.5.210:3000/admin</strong> → <strong>Server → Plugin Config</strong> → <strong>BoatWise Cloud Sync</strong>
        </p>
        <InfoGrid style={{ marginTop: '1rem' }}>
          <InfoRow>
            <InfoLabel>API Endpoint:</InfoLabel>
            <InfoValue mono>https://app.boatwise.ca</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Vessel Token:</InfoLabel>
            <InfoValue>Copy from vessel card above</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Enable Streaming:</InfoLabel>
            <InfoValue>✓ Checked (optional — batch mode is enough)</InfoValue>
          </InfoRow>
        </InfoGrid>

        <h4 style={{ color: theme.colors.text.primary, marginTop: '1.5rem', marginBottom: '0.5rem' }}>
          Activate & verify
        </h4>
        <p style={{ color: theme.colors.text.secondary, marginBottom: '0.5rem' }}>
          Check <strong>Active</strong>, click <strong>Submit</strong>, restart SignalK. Logs must show <strong>v2.0 Dual Mode</strong> (not &quot;connection test 404&quot;).
        </p>
        <p style={{ color: theme.colors.text.secondary, margin: 0, fontSize: '0.875rem', fontStyle: 'italic' }}>
          Full docs: repo <code>signalk-to-boatwise-plugin/INSTALLATION.md</code>
        </p>
      </InstallGuide>
    </Container>
  );
};

export default SignalKPluginManager;

